import type { MouseEvent } from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import {
  Location20Regular,
  CalendarLtr20Regular,
  Clock20Regular,
  Edit20Regular,
  Info20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { StatusTag } from '../StatusTag';
import { getEventActions } from '@/types/events.types';
import { useGetBuildingByIdQuery } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import type { BuildingId } from '@/store/models/ids';
import { formatDate } from '@/utils/dateFormat';
import { BOOKING_FORM_VISIBLE_STEP_COUNT } from '@/store/models/booking';
import { formatUtcDateInTimeZone, formatUtcTimeInTimeZone } from '@/utils/timeZone';
import type { RequestCardProps } from '@/types/events.types';
import { useRequestCardStyles } from './RequestCard.styles';

/**
 * Format date in the building's timezone using the central pattern.
 */
const formatEventDate = (dateString: string, timeZone?: string): string =>
  formatUtcDateInTimeZone(dateString, timeZone);

/**
 * Format time range for display (e.g., "10:00 AM - 2:00 PM")
 */
const formatTimeRange = (
  startDate: string,
  endDate: string,
  locale: string,
  timeZone?: string,
  timeZoneShort?: string
): string => {
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  const start = formatUtcTimeInTimeZone(startDate, locale, timeZone, formatOptions);
  const end = formatUtcTimeInTimeZone(endDate, locale, timeZone, formatOptions);
  const tz = timeZoneShort ? ` ${timeZoneShort}` : '';
  return `${start} - ${end}${tz}`;
};

export const RequestCard = ({
  event,
  onView,
  onDelete,
  onCancel,
  onViewReason,
  figmaVariant = false,
}: RequestCardProps) => {
  const styles = useRequestCardStyles();
  const { t, i18n } = useTranslation('dashboard');
  const isDraft = event.eventState === 'DRAFT';
  const { data: buildingData } = useGetBuildingByIdQuery(event.buildingId as BuildingId, {
    skip: !event.buildingId || isDraft,
  });
  const actions = getEventActions(event.eventState);
  const buildingTimeZone = buildingData?.data?.gmtZone;
  const buildingTimeZoneShort = buildingData?.data?.GMTZoneShort;

  const displayTitle = event.eventTitle || (isDraft ? t('card.untitledDraft') : '');

  // Multi-space bookings: show the first space name and append "+N" when the
  // event reserves more than one space. Falls back to the legacy singular
  // `space` object for single-space events.
  const spaceNames = (event.spaces ?? [])
    .map((s) => s.spaceName)
    .filter((name): name is string => Boolean(name));
  const primarySpaceName = spaceNames[0] ?? event.space?.spaceName;
  const extraSpaceCount = spaceNames.length > 1 ? spaceNames.length - 1 : 0;
  const locationText = [event.buildingName, primarySpaceName].filter(Boolean).join(', ');

  const stepIndexMap: Record<string, number> = {
    locationDateTime: 1,
    spaceSelection: 2,
    eventDetails: 3,
    additionalDetails: 4,
    review: 5,
  };

  // spec 101 US4 (FR-009, FR-010, SC-004): step number must match the step the
  // form resumes on. `event.currentStep` is persisted on every draft save in
  // useBookingForm; default to step 1 when the id is missing or unknown so the
  // tile renders sensibly without the prior off-by-one heuristic.
  const draftStepCompleted = isDraft
    ? (event.currentStep && stepIndexMap[event.currentStep]) || 1
    : 0;

  const handleView = () => onView(event.eventId);
  const handleDelete = () => onDelete?.(event.seriesId ?? event.eventId);
  const handleCancel = () => onCancel?.(event.eventId);
  const handleViewReason = () => onViewReason?.(event.eventId);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <article
      className={mergeClasses(
        styles.card,
        figmaVariant && styles.cardFigma,
        isDraft && styles.draftCard
      )}
      aria-label={displayTitle}
      onClick={handleView}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleView();
        }
      }}
    >
      {/* Content Area — 3-row grid (spec 104 US3):
          row 1: title + status; row 2: info items (left) + primary action (right);
          row 3: request metadata (left) + secondary action (right). */}
      <div className={styles.contentArea}>
        {/* Row 1 */}
        <div className={styles.headerRow}>
          <Text className={styles.title}>{displayTitle}</Text>
          <StatusTag state={event.eventState} />
        </div>

        {/* Row 2 — info items (left column) */}
        <div className={mergeClasses(styles.infoRow, isDraft && styles.infoRowDraft)}>
          {isDraft ? (
            <>
              <div className={mergeClasses(styles.infoItem, styles.infoItemDraftStep)}>
                <Edit20Regular className={styles.icon} aria-hidden="true" />
                <Text className={styles.infoText}>
                  {t('card.draftProgress', {
                    step: draftStepCompleted,
                    total: BOOKING_FORM_VISIBLE_STEP_COUNT,
                  })}
                </Text>
              </div>
              <div className={mergeClasses(styles.infoItem, styles.infoItemDraftSaved)}>
                <Clock20Regular className={styles.icon} aria-hidden="true" />
                <Text className={styles.infoText}>
                  {t('card.saved', { date: formatDate(event.lastUpdatedAt) })}
                </Text>
              </div>
            </>
          ) : (
            <>
              <div className={mergeClasses(styles.infoItem, styles.infoItemLocation)}>
                <Location20Regular className={styles.icon} aria-hidden="true" />
                <Text className={mergeClasses(styles.infoText, styles.wrappingText)}>
                  {locationText}
                  {extraSpaceCount > 0 && (
                    <Text as="span" className={styles.moreSpaces} title={spaceNames.join(', ')}>
                      {' '}
                      {t('card.moreSpaces', { count: extraSpaceCount })}
                    </Text>
                  )}
                </Text>
              </div>
              <div className={mergeClasses(styles.infoItem, styles.infoItemDate)}>
                <CalendarLtr20Regular className={styles.icon} aria-hidden="true" />
                <Text className={styles.infoText}>
                  {formatEventDate(event.eventStartDate, buildingTimeZone)}
                </Text>
              </div>
              <div className={mergeClasses(styles.infoItem, styles.infoItemTime)}>
                <Clock20Regular className={styles.icon} aria-hidden="true" />
                <Text className={styles.infoText}>
                  {formatTimeRange(
                    event.eventStartDate,
                    event.eventEndDate,
                    i18n.language,
                    buildingTimeZone,
                    buildingTimeZoneShort
                  )}
                </Text>
              </div>
              {event.cancelledReason && (
                <div className={mergeClasses(styles.infoItem, styles.infoItemReason)}>
                  <Info20Regular className={styles.icon} aria-hidden="true" />
                  <Text className={mergeClasses(styles.infoText, styles.reasonText)}>
                    <Text as="span" className={styles.reasonLabel}>
                      {t('card.cancellationReason')}
                    </Text>{' '}
                    {event.cancelledReason}
                  </Text>
                </div>
              )}
            </>
          )}
        </div>

        {/* Row 3 — request metadata (left column).
            Drafts don't have a request id yet, so the left cell is omitted
            for them; the actions group on the right stays anchored to the
            same row position regardless. */}
        {!isDraft && (
          <div className={styles.secondaryRow}>
            <Text className={mergeClasses(styles.secondaryText, styles.wrappingText)}>
              {event.submittedDate
                ? t('card.requestInfo', {
                    requestId: event.requestId,
                    date: formatDate(event.submittedDate),
                  })
                : t('card.requestIdOnly', { requestId: event.requestId })}
            </Text>
          </div>
        )}

        {/* Actions group (right column).
            Both the primary CTA and the state-appropriate secondary action
            live side-by-side here so their right edges line up. For
            submitted/past cards this anchors to row 3 (alongside the
            request id metadata); for drafts (which have no row 3) it sits
            on row 2 next to the step + saved-date info. spec 104 US2 / US3. */}
        <div
          className={mergeClasses(styles.actionsGroup, isDraft && styles.draftActionsGroup)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {isDraft ? (
            <>
              <BasicButton
                className={styles.actionButton}
                variant="primary"
                size="medium"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleView();
                }}
              >
                {t('actions.continueEditing')}
              </BasicButton>
              {onDelete && (
                <BasicButton
                  className={styles.secondaryActionButton}
                  variant="outline"
                  size="medium"
                  onClick={handleDelete}
                >
                  {t('actions.delete')}
                </BasicButton>
              )}
            </>
          ) : (
            <>
              <BasicButton
                className={styles.actionButton}
                variant="primary"
                size="medium"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleView();
                }}
              >
                {t('actions.viewDetails')}
              </BasicButton>
              {/* Request Cancellation is always rendered for non-draft cards so the
                  primary "View Details" button keeps the same horizontal position
                  across every event state. The button is disabled when the event
                  is no longer cancellable (e.g., already declined or cancelled). */}
              {onCancel && (
                <BasicButton
                  className={styles.secondaryActionButton}
                  variant="outline"
                  size="medium"
                  disabled={!actions.canCancel}
                  onClick={handleCancel}
                >
                  {t('actions.cancel')}
                </BasicButton>
              )}
              {actions.canViewReason && onViewReason && (
                <BasicButton
                  className={styles.secondaryActionButton}
                  variant="outline"
                  size="medium"
                  onClick={handleViewReason}
                >
                  {t('actions.viewReason')}
                </BasicButton>
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

RequestCard.displayName = 'RequestCard';
